import { createElement } from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import ACL_RELATIONSHIPS from "acl-relationships";
import { formatDate } from "components/helpers";
import withRoleCheck from "components/hocs/withRoleCheck";
import _DeleteManualActivityModal from "components/modals/DeleteManualActivityModal";
import NoteContent from "components/NoteContent";
import AttachmentsMenu from "components/LogItem/elements/AttachmentsMenu";
import EntrySubject from "components/LogItem/elements/EntrySubject";
import MergedBadge from "components/LogItem/elements/MergedBadge";
import { getMessagePart } from "components/LogItem/messages/emailMessagePart";

const DeleteManualActivityModal = withRoleCheck(_DeleteManualActivityModal, [
  ACL_RELATIONSHIPS.campaignTargetCall.delete,
  ACL_RELATIONSHIPS.campaignTargetEmail.delete,
  ACL_RELATIONSHIPS.textMessage.delete,
]);

// Notes written from the Notes tab during a call, stamped with its SID.
const CallNotes = ({ notes }) => (
  <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-2">
    {notes.map(note => (
      <div
        key={note.id}
        className="tw:rounded-md tw:border tw:border-(--color-border) tw:bg-(--color-surface) tw:px-3 tw:py-2"
      >
        <div className="tw:text-sm tw:text-(--color-text-secondary)">
          <strong className="tw:text-(--color-text-primary)">
            {note.creator_user_full_name}
          </strong>{" "}
          {formatDate(note.created_at)}
        </div>
        <NoteContent
          note={note}
          className="tw:mt-1 tw:max-h-96 tw:overflow-y-auto"
        />
      </div>
    ))}
  </div>
);

const LogEntryCard = ({
  ref,
  item,
  entry: { title, Icon, message, actions },
  modelType,
  modelId,
  activeItem,
  dispositions,
  refreshLog,
}) => {
  const {
    auditable_id,
    entity_id,
    contact_id,
    id,
    ctfr_id,
    ctfr_info,
    call_notes,
    form_language,
    info,
  } = item;
  const EntryIcon = Icon ?? Activity;
  const email = getMessagePart(info.message, "email");
  const campaign = {
    id: item.campaign_id,
    name: item.campaign_name,
    deleted: item.campaign_deleted,
  };
  const isDeleted = item.action === "deleted";
  const mergedFromContacts = info._merged_from_contact;
  const mergedFromEntities = info._merged_from_entity;
  const attachments = item.attachments ?? [];
  // The campaign's own feed already names the campaign; its rows need their
  // entity or contact instead.
  const isCampaignFeed = modelType === "Campaign";

  return (
    <Card
      size="sm"
      ref={ref}
      className={cn("tw:w-full", activeItem && "highlight")}
    >
      <CardContent>
        <div className="tw:flex tw:gap-3">
          <EntryIcon
            className="tw:mt-0.5 tw:size-6 tw:shrink-0 tw:text-(--color-text-secondary)"
            aria-hidden="true"
          />
          <div className="tw:min-w-0 tw:flex-1">
            <div className="tw:flex tw:items-start tw:gap-2">
              <div className="tw:min-w-0 tw:flex-1">
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                  <h4 className="tw:m-0! tw:leading-5 tw:[font-family:var(--font-heading)] tw:text-sm tw:font-semibold tw:text-(--color-text-primary)">
                    {title}
                  </h4>
                  {info.is_manual && <Badge size="sm">Manual</Badge>}
                  {(mergedFromContacts || mergedFromEntities) && (
                    <MergedBadge
                      mergedFromContacts={mergedFromContacts}
                      mergedFromEntities={mergedFromEntities}
                    />
                  )}
                </div>
                <div className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-2 tw:text-sm tw:text-(--color-text-secondary)">
                  <span>{formatDate(item.created_at)}</span>
                  {isCampaignFeed && <EntrySubject item={item} />}
                  {campaign.id && !isCampaignFeed && (
                    <span>
                      Campaign:{" "}
                      {campaign.deleted ? (
                        campaign.name
                      ) : (
                        <Link to={`/campaigns/${campaign.id}`}>
                          {campaign.name}
                        </Link>
                      )}
                    </span>
                  )}
                </div>
              </div>
              {info.is_manual && !isDeleted && (
                <div className="tw:shrink-0">
                  <DeleteManualActivityModal
                    item={item}
                    fetchData={refreshLog}
                  />
                </div>
              )}
            </div>

            {message && (
              <div
                data-slot="log-entry-message"
                className="tw:mt-3 tw:flex tw:flex-col tw:gap-1 tw:break-words tw:text-(--color-text-primary) tw:[&_p]:m-0!"
              >
                {message}
              </div>
            )}

            {call_notes.length > 0 && <CallNotes notes={call_notes} />}

            {!isDeleted && (actions || attachments.length > 0) && (
              <div className="tw:mt-3 tw:flex tw:flex-wrap tw:gap-2">
                {actions &&
                  createElement(actions, {
                    campaign_id: campaign.id,
                    entity_id,
                    email,
                    logId: id,
                    modelType,
                    modelId,
                    ctfr_id,
                    ctfr_info,
                    form_language,
                    contact_id,
                    auditable_id,
                    dispositions,
                    item,
                    refreshLog,
                  })}
                {attachments.length > 0 && (
                  <AttachmentsMenu attachments={attachments} />
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LogEntryCard;
